/**
 * O alerta que chega enquanto a tela está aberta.
 *
 * ═══ ISTO EXISTIA E SUMIU NA REESCRITA ═══
 *
 * O painel anterior ao React consultava `/api/notificacoes/novas` em
 * pulso e avisava. A rota continuou no servidor, sem ninguém chamar, por
 * meses — e quem deixa a tela aberta numa portaria simplesmente deixou de
 * ser avisado, sem nada na tela dizendo que aquilo tinha existido.
 *
 * ═══ DOIS AVISOS, PORQUE SÃO DOIS CASOS ═══
 *
 *   painel À VISTA      → aviso desenhado por nós, dentro da página:
 *                         foto, cor, clique que abre o evento.
 *   painel ESCONDIDO    → notificação do sistema operacional. Feia — ela
 *                         não aceita estilo, e isso é proposital: se um
 *                         site pudesse desenhá-la, teria como imitar uma
 *                         janela de sistema. Mas é a única que atravessa
 *                         a janela minimizada.
 *
 * Mostrar as duas ao mesmo tempo seria o mesmo alerta duas vezes.
 *
 * ═══ O PRIMEIRO PULSO NÃO AVISA ═══
 *
 * Ao abrir, pergunta com `desde=0`, que devolve vazio de propósito e só
 * diz qual é o último id. Sem isso, abrir o painel de manhã dispararia a
 * noite inteira de uma vez.
 */

import { useEffect, useRef } from 'react'
import { api, url } from '../api/cliente'

/** Cada quanto perguntar. 8 s: o alerta é para agora, e a resposta é
 *  minúscula (só o que chegou depois do último id que já vimos). */
const PULSO_MS = 8000

export interface AvisoAoVivo {
  id: number
  titulo: string
  texto: string
  foto: string | null
  ts: string
  canal: string
}

interface Resposta {
  ultimo: number
  avisos: AvisoAoVivo[]
}

export function podeNotificar() {
  return typeof Notification !== 'undefined'
    && Notification.permission === 'granted'
}

export async function pedirPermissao(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false
  if (Notification.permission === 'granted') return true
  /* Negado uma vez, o navegador não pergunta de novo — e insistir aqui
     não faz nada. Quem negou tem de mudar nas configurações do site. */
  if (Notification.permission === 'denied') return false
  return (await Notification.requestPermission()) === 'granted'
}

function notificarPeloSistema(a: AvisoAoVivo) {
  const n = new Notification(a.titulo || 'Aviso', {
    body: a.texto || '',
    // A foto do recorte como ícone: dá para reconhecer o que aconteceu
    // sem abrir nada.
    icon: a.foto ? url(`/imagens/${a.foto}`) : undefined,
    // A `tag` junta repetidos numa notificação só, em vez de empilhar dez
    // iguais quando a mesma automação dispara em sequência.
    tag: a.titulo,
    // Fica até alguém dispensar: num alerta de segurança, sumir sozinho
    // em 5 segundos é o mesmo que não ter avisado.
    requireInteraction: true,
  })
  n.onclick = () => { window.focus(); n.close() }
}

export function usarAvisosAoVivo(aoChegar: (a: AvisoAoVivo) => void) {
  const ultimo = useRef(0)
  const comecou = useRef(false)

  useEffect(() => {
    let vivo = true

    async function pulso() {
      try {
        const d = await api.ler<Resposta>(
          `/api/notificacoes/novas?desde=${ultimo.current}`)
        if (!vivo) return
        if (comecou.current) {
          for (const a of d.avisos ?? []) {
            /* Aba escondida é o caso em que o aviso do sistema é a única
               coisa que chega. Com a aba à vista, quem desenha somos nós
               — ver o cabeçalho. */
            if (document.visibilityState === 'visible') aoChegar(a)
            else if (podeNotificar()) notificarPeloSistema(a)
          }
        }
        ultimo.current = d.ultimo || ultimo.current
        comecou.current = true
      } catch {
        /* Pulso que falha não pode virar erro na tela: a rede caindo por
           8 segundos não é assunto de quem está olhando a galeria. */
      }
    }

    void pulso()
    const t = setInterval(pulso, PULSO_MS)
    return () => { vivo = false; clearInterval(t) }
  }, [aoChegar])
}
